import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

/**
 * Hook that queues items until a condition is met, then flushes them.
 * Useful for delaying actions until dependencies have loaded.
 *
 * @template T - The type of item that the queue will hold.
 * @param {boolean} ready - When true, the queue flushes and new items are sent immediately.
 * @param {(items: T[]) => void} onFlush - Function that processes the queued items.
 * @returns {(item: T) => void} Function to add an item to the queue.
 *
 * @example
 * const addToQueue = useQueueUntilReady(
 *   scriptLoaded,
 *   (items) => items.forEach(processItem)
 * );
 *
 * addToQueue(myData); // queues if not ready, sends immediately if ready
 */
export function useQueueUntilReady(ready, onFlush) {
  const queueRef = useRef(/** @type {T[]} */ ([]));

  // using stable ref to prevent re-renders when onFlush changes
  // @see https://www.epicreact.dev/the-latest-ref-pattern-in-react
  const onFlushRef = useRef(onFlush);

  // Keep ref updated with latest callback
  useLayoutEffect(() => {
    onFlushRef.current = onFlush;
  }, [onFlush]);

  const flushQueue = useCallback(() => {
    if (!ready || queueRef.current.length === 0) {
      return;
    }

    const itemsToFlush = [...queueRef.current];
    queueRef.current = [];
    onFlushRef.current(itemsToFlush);
  }, [ready]);

  useEffect(() => {
    if (ready) {
      flushQueue();
    }
  }, [ready, flushQueue]);

  /**
   * Adds an item to the queue, or sends it immediately if ready.
   */
  const addToQueue = useCallback(
    /**
     * @param {T} item - The item to enqueue or immediately flush
     */
    (item) => {
      if (ready) {
        // Note: The `onFlush` handler expects an array of items
        onFlushRef.current([item]);
      } else {
        queueRef.current.push(item);
      }
    },
    [ready],
  );

  return addToQueue;
}
